// R-131-FIN-DECISION-F — headline metrics for side-by-side pathway comparison.

import { applyPathwayCarbonAdjust } from './pathwayCarbonAdjust';
import {
  computePathwayLcoe,
  computePathwayPhaseLcoeBreakdown,
  type PathwayPhaseLcoe,
} from './pathwayLcoe';
import {
  computePathwayIrrPct,
  computePathwayNpvUsd,
  DEFAULT_COMPARE_MONTE_CARLO_OPTS,
  runPathwayMonteCarlo,
  type MonteCarloResult,
} from './pathwayMonteCarlo';
import { derivePathwayTimeline, pathwayDiscountRateFraction } from './pathwayCashflows';
import { pathwayTimeToPowerYears } from './pathwayQuickEdit';
import { riskAdjustedWaccFromPathway } from './riskPremium';
import type { Pathway } from './types';
import type { ProjectIntent } from '../../types/siteRag';

export type PathwayHeadlineTone = 'positive' | 'neutral' | 'negative';

export interface PathwayHeadline {
  label: string;
  tone: PathwayHeadlineTone;
}

export interface PathwayCompareMetrics {
  pathwayId: string;
  name: string;
  lcoeRealUsdPerMwh: number;
  lcoeP10: number;
  lcoeP90: number;
  /**
   * Project NPV@WACC (USD) on a VALUE-CONSISTENT basis: every pathway — INCLUDING
   * the grid-tariff baseline — is credited the SAME value-of-served-load per MWh
   * (see VALUE_OF_SERVED_LOAD_USD_PER_MWH / valueOfServedLoadRevenue in
   * presets/shared.ts), so NPV = value − own cost is comparable across pathways
   * and the NPV ranking tracks the LCOE ranking (lower cost ⇒ higher /
   * less-negative NPV).
   *
   * RESIDUAL HORIZON SKEW (documented caveat): the credit is now consistent, but
   * the NPVs are NOT life-equalised — pathways have different design lives (e.g.
   * grid 30 yr, SMR 40 yr), and a longer-lived asset accrues more discounted
   * value-minus-cost years, so a small horizon advantage remains baked in. Use
   * `lcoeRealUsdPerMwh` (already life-normalised per MWh) as the tie-breaker when
   * two pathways' NPVs are close and their lifetimes differ. No perpetuity /
   * terminal value is added.
   */
  npvUsd: number;
  npvP10: number;
  npvP90: number;
  irrPct: number;
  irrP10: number;
  irrP90: number;
  timeToPowerYears: number;
  lifetimeLabel: string;
  annualTCo2e: number;
  /**
   * PRESENT VALUE of the pathway's whole operating emissions stream, in tCO2e,
   * discounted at the same rate and from the same instant as `npvUsd`.
   *
   * C14 — ranking used `annualTCo2e` directly, i.e. it subtracted ONE YEAR of
   * carbon cost from a LIFETIME NPV. The two are not commensurate, and the
   * penalty came out roughly an order of magnitude too small, so a carbon
   * commitment barely moved the order it was supposed to govern. Multiplying
   * this by a $/tCO2e price gives a figure in the same units as the NPV it is
   * subtracted from.
   *
   * Discounted from NOW (not from COD), matching `npvUsd`'s cohort-year
   * convention — so a pathway that starts emitting later is correctly credited
   * for the delay.
   */
  carbonPvTCo2e: number;
  riskPremiumBps: number;
  headline: PathwayHeadline;
  monteCarlo: MonteCarloResult;
  /**
   * R-131-FIN-DECISION-PHASE-LCOE — ADDITIVE per-phase LCOE attribution.
   * Single-phase pathways carry a one-element breakdown spanning the whole
   * lifecycle (no change to the headline `lcoeRealUsdPerMwh`). Multi-phase /
   * multi-cohort pathways split per build-then-run cohort so the comparison can
   * show WHERE cost accumulates. (Q7: only persisted pathways carry that shape
   * today — the hybrid-bridge preset was retired.) The energy-PV-weighted blend of these
   * per-phase LCOEs reconciles to `lcoeRealUsdPerMwh` (whole-pathway real LCOE).
   */
  phaseBreakdown: PathwayPhaseLcoe[];
}

const HEADLINE_POSITIVE: PathwayHeadline = { label: '🟢 Fastest', tone: 'positive' };
const HEADLINE_NEGATIVE: PathwayHeadline = { label: '🔴 Long lock', tone: 'negative' };
const HEADLINE_NEUTRAL: PathwayHeadline = { label: '🟡 Mid-term', tone: 'neutral' };

function lifetimeLabel(pathway: Pathway): string {
  if (pathway.technology === 'grid-tariff') return 'indefinite';
  return `${pathway.lifetime.designLifeYears} yr`;
}

function assignHeadlines(metrics: PathwayCompareMetrics[]): PathwayCompareMetrics[] {
  if (metrics.length === 0) return metrics;

  const byTime = [...metrics].sort((a, b) => a.timeToPowerYears - b.timeToPowerYears);
  const fastestId = byTime[0]?.pathwayId;
  const slowestId = byTime[byTime.length - 1]?.pathwayId;

  return metrics.map((row) => {
    if (row.pathwayId === fastestId && metrics.length > 1) {
      return { ...row, headline: HEADLINE_POSITIVE };
    }
    if (row.pathwayId === slowestId && metrics.length > 2) {
      return { ...row, headline: HEADLINE_NEGATIVE };
    }
    if (metrics.length === 2 && row.pathwayId === slowestId) {
      return { ...row, headline: HEADLINE_NEGATIVE };
    }
    return { ...row, headline: HEADLINE_NEUTRAL };
  });
}

/**
 * PV of the operating emissions stream, in tCO2e.
 *
 * Emissions accrue over the same window the cashflow builder charges opex and
 * books revenue — the half-open [COD, COD + ppaTermYears) operating horizon —
 * and are discounted from `now` at the pathway's own risk-adjusted rate, so the
 * result is directly commensurate with `npvUsd`.
 *
 * Discounting a physical quantity is a modelling choice, not a physical claim:
 * it is done here only so the ranking penalty (tonnes × $/t) lands in the same
 * present-value units as the NPV it adjusts. A zero-emission pathway returns 0.
 */
function carbonPvTCo2e(pathway: Pathway, annualTCo2e: number, now: Date): number {
  if (!Number.isFinite(annualTCo2e) || annualTCo2e === 0) return 0;
  const timeline = derivePathwayTimeline(pathway, now);
  const rate = pathwayDiscountRateFraction(pathway);
  const years = Math.max(0, Math.floor(pathway.revenue.ppaTermYears));
  let pv = 0;
  for (let i = 0; i < years; i += 1) {
    const cohortYear = timeline.codCohortYear + i;
    pv += annualTCo2e / Math.pow(1 + rate, cohortYear);
  }
  return pv;
}

/** Compute deterministic + Monte Carlo headline metrics for one pathway. */
export function computePathwayCompareMetrics(
  pathway: Pathway,
  opts?: {
    now?: Date;
    carbonCommitment?: ProjectIntent['carbonCommitment'];
    carbonPricePerTUsd?: number;
    monteCarloRuns?: number;
    monteCarloSeed?: number;
  },
): PathwayCompareMetrics {
  const now = opts?.now ?? new Date();
  const mc = runPathwayMonteCarlo(pathway, {
    ...DEFAULT_COMPARE_MONTE_CARLO_OPTS,
    runs: opts?.monteCarloRuns ?? 400,
    seedRng: opts?.monteCarloSeed ?? hashPathwayId(pathway.id),
    now,
  });

  const lcoe = computePathwayLcoe(pathway, { now });
  const phaseLcoe = computePathwayPhaseLcoeBreakdown(pathway, { now });
  const carbon = applyPathwayCarbonAdjust(pathway, {
    carbonCommitment: opts?.carbonCommitment,
    carbonPricePerTUsd: opts?.carbonPricePerTUsd,
    applyCarbonOverlay: false,
  });
  const wacc = riskAdjustedWaccFromPathway(pathway);

  return {
    pathwayId: pathway.id,
    name: pathway.name,
    lcoeRealUsdPerMwh: lcoe.lcoeRealPerMwhUsd,
    lcoeP10: mc.summary.lcoe.p10,
    lcoeP90: mc.summary.lcoe.p90,
    npvUsd: computePathwayNpvUsd(pathway, now),
    npvP10: mc.summary.npv.p10,
    npvP90: mc.summary.npv.p90,
    irrPct: computePathwayIrrPct(pathway, now),
    irrP10: mc.summary.irr.p10,
    irrP90: mc.summary.irr.p90,
    timeToPowerYears: pathwayTimeToPowerYears(pathway, now),
    lifetimeLabel: lifetimeLabel(pathway),
    annualTCo2e: carbon.annualTCo2e,
    carbonPvTCo2e: carbonPvTCo2e(pathway, carbon.annualTCo2e, now),
    riskPremiumBps: wacc.premiumComponents.reduce((sum, c) => sum + c.bps, 0),
    headline: HEADLINE_NEUTRAL,
    monteCarlo: mc,
    phaseBreakdown: phaseLcoe.phaseBreakdown,
  };
}

export function computeAllPathwayCompareMetrics(
  pathways: Pathway[],
  opts?: Parameters<typeof computePathwayCompareMetrics>[1],
): PathwayCompareMetrics[] {
  const rows = pathways.map((p) => computePathwayCompareMetrics(p, opts));
  return assignHeadlines(rows);
}

function hashPathwayId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) {
    h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h) || 1;
}

/**
 * "No data" glyph for non-finite metrics. After R-131-FIN-DECISION-D made
 * IRR/LCOE return NaN for non-converging / empty-cashflow pathways (instead of
 * silently rendering a misleading 0), every numeric formatter that can receive
 * those values must guard — otherwise the comparison card prints "NaN%".
 */
const METRIC_NA = '—';

export function formatUsdPerMwh(value: number): string {
  if (!Number.isFinite(value)) return METRIC_NA;
  return `$${Math.round(value)}/MWh`;
}

export function formatUsdCompactMillions(value: number): string {
  if (!Number.isFinite(value)) return METRIC_NA;
  const millions = value / 1_000_000;
  const sign = millions >= 0 ? '+' : '−';
  return `${sign}$${Math.abs(millions).toFixed(0)}M`;
}

export function formatPct(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return METRIC_NA;
  return `${value.toFixed(digits)}%`;
}

export function formatRange(
  low: number,
  high: number,
  formatter: (v: number) => string,
): string {
  return `${formatter(low)}..${formatter(high)}`;
}

export function formatRiskPremiumBps(bps: number): string {
  return `+${bps}bps`;
}

export function formatAnnualEmissions(annualTCo2e: number): string {
  if (annualTCo2e <= 0) return '0';
  if (annualTCo2e >= 1000) {
    return `${Math.round(annualTCo2e / 1000)}k`;
  }
  return Math.round(annualTCo2e).toLocaleString('en-US');
}
